import { RollbackOutlined, SaveOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Col, message, Modal, Row, Tabs } from "antd";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import { t, CoreResource } from "shared/plugins/localization";
import { setLoading } from "states/global-value/global-value.slice";
import {
  addBranch,
  getBranchById,
  updateBranch,
} from "states/branch/branch.api";
import {
  CreateBranchModel,
  BranchModel,
  UpdatedBranchModel,
} from "states/branch/branch.models";
import { ADD_BRANCH_ROUTE, BRANCH_ROUTE } from "shared/constants/routes";
import { WareHouseModel } from "states/ware-house/ware-house.models";
import { CustomTheme } from "shared/components/custom-theme/custom-theme";
import { Seo } from "shared/components/seo/seo";
import { SeoModel } from "shared/models/seo-model";
import { fetchWareHouseList } from "states/ware-house/ware-house.slice";
import { BranchInformationTab } from "./components/branch-information-tab";
import useRefresh from "shared/hooks/use-refresh";
import { BranchPaymentSettingTab } from "./components/branch-payment-setting-tab";
import { BranchOrderPickupTab } from "./components/branch-order-pickup-tab";
import { BranchScheduleTab } from "./components/branch-schedule-tab";

interface RouteParams {
  branchId: string;
}
interface IAddUpdateBranchProps {}

export function AddUpdateBranch(props: IAddUpdateBranchProps) {
  const { branchId } = useParams<RouteParams>();
  const [model, setModel] = useState<BranchModel>({
    wareHouses: [] as WareHouseModel[],
    address: {},
  } as BranchModel);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const dispatch = useDispatch();
  const refresh = useRefresh();

  const getBranch = async () => {
    try {
      const res = await getBranchById(Number(branchId));
      res.data.address = res.data.address ?? {};
      setModel(res.data);
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message ??
          "Lỗi hệ thống"
      );
    }
  };

  const onSaved = async () => {
    dispatch(setLoading(true));
    try {
      if (isUpdateMode) {
        const payload = {
          id: model.id,
          name: model.name,
          address: model.address,
          email: model.email,
          isActive: model.isActive,
          isUpComing: model.isUpComing,
          phone: model.phone,
          wareHouseIds: model.wareHouses.map((m) => m.id),
          slug: model.slug,
          defaultPrinterIp: model.defaultPrinterIp,
          timeZone: model.timeZone,
          customizeTheme: model.customizeTheme ?? {},
          seoFields: model.seoFields,
          taxRate: model.taxRate,
          sort: model.sort,
        } as UpdatedBranchModel;
        await updateBranch(payload);
      } else {
        const payload = {
          name: model.name,
          address: model.address,
          email: model.email,
          isActive: model.isActive,
          isUpComing: model.isUpComing,
          phone: model.phone,
          wareHouseIds: model.wareHouses.map((m) => m.id),
          slug: model.slug,
          defaultPrinterIp: model.defaultPrinterIp,
          timeZone: model.timeZone,
          customizeTheme: model.customizeTheme ?? {},
          seoFields: model.seoFields,
          taxRate: model.taxRate,
          sort: model.sort,
        } as CreateBranchModel;
        await addBranch(payload);
      }
      Modal.success({
        title: "",
        content: "Cập nhật thành công",
        afterClose: refresh,
      });
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message ??
          "Lỗi hệ thống"
      );
    }
    dispatch(setLoading(false));
  };

  useEffect(() => {
    dispatch(fetchWareHouseList());
    if (document.location.href.indexOf(ADD_BRANCH_ROUTE) > -1) {
      setIsUpdateMode(false);
    } else {
      setIsUpdateMode(true);
      getBranch();
    }
  }, []);

  return (
    <div className="add-update-branch">
      <Row gutter={[16, 16]} className="mb-4">
        <Col flex="auto">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={BRANCH_ROUTE}>{"Chi nhánh"}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{isUpdateMode ? "Cập nhật chi nhánh "+ model?.name:"Thêm chi nhánh"}</Breadcrumb.Item>
          </Breadcrumb>
        </Col>
        <Col flex="auto" className="d-flex justify-content-end">
          <Link to={BRANCH_ROUTE} className="mr-2">
            <Button type="default" icon={<RollbackOutlined />}>
              {"Quay lại"}
            </Button>
          </Link>
          <Button onClick={onSaved} type="primary" icon={<SaveOutlined />}>
            {"Lưu"}
          </Button>
        </Col>
      </Row>
      <Tabs type="card">
        <Tabs.TabPane tab="Information" key="1">
          <BranchInformationTab
            model={model}
            onChange={(newModel) => {
              setModel(newModel);
            }}
            isUpdateMode
          />
        </Tabs.TabPane>
        {isUpdateMode && (
          <>
            <Tabs.TabPane tab="Schedule" key="schedule">
              <BranchScheduleTab branchId={model.id} />
            </Tabs.TabPane>
            <Tabs.TabPane tab="Nhà vận chuyển" key="payment-gateway">
              <BranchPaymentSettingTab branchId={model.id} />
            </Tabs.TabPane>
            <Tabs.TabPane tab="Payment Gateways" key="payment-gateway">
              <BranchPaymentSettingTab branchId={model.id} />
            </Tabs.TabPane>
            <Tabs.TabPane tab="Order-Pickup Settings" key="order-pickup">
              <BranchOrderPickupTab branchId={model.id} />
            </Tabs.TabPane>
          </>
        )}
        <Tabs.TabPane tab="Website Theme" key="giao-dien">
          <CustomTheme
            onChange={(changedModel) => {
              setModel({ ...model, customizeTheme: changedModel });
            }}
            model={model.customizeTheme ?? {}}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="SEO" key="seo">
          <Seo
            model={model.seoFields ?? ({} as SeoModel)}
            slug={model.slug}
            onChange={(seoFields, slug) => {
              setModel({ ...model, slug, seoFields });
            }}
          />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
}
